import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, ClipboardList, Loader2, CheckCircle2, XCircle, AlertTriangle, ShieldAlert, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getAuthHeaders } from "@/lib/auth-helper";
import { listEventValidations } from "@/lib/validations.functions";

export const Route = createFileRoute("/dashboard/events/$eventId/validations")({
  head: () => ({ meta: [{ title: "Historial de validaciones — ZYNTRA" }] }),
  component: ValidationsHistoryPage,
});

type Validation = {
  id: string;
  ticketId: string | null;
  eventId: string | null;
  validatedByUserId: string | null;
  validatedByRole: string | null;
  result: "valid" | "used" | "invalid" | "forbidden" | string;
  message: string | null;
  qrData: string | null;
  ticketStatusBefore: string | null;
  ticketStatusAfter: string | null;
  ticketLabel: string | null;
  validatedAt: string;
  validatorDisplayName: string | null;
  validatorEmail: string | null;
};

const RESULT_OPTIONS = [
  { value: "", label: "Todos los resultados" },
  { value: "valid", label: "Válidos" },
  { value: "used", label: "Ya usados" },
  { value: "invalid", label: "Inválidos" },
  { value: "forbidden", label: "Sin permisos" },
];

function resultMeta(r: string) {
  switch (r) {
    case "valid":
      return { Icon: CheckCircle2, cls: "text-success border-success/30 bg-success/10", label: "Válido" };
    case "used":
      return { Icon: AlertTriangle, cls: "text-warning border-warning/30 bg-warning/10", label: "Ya usado" };
    case "forbidden":
      return { Icon: ShieldAlert, cls: "text-destructive border-destructive/30 bg-destructive/10", label: "Sin permisos" };
    default:
      return { Icon: XCircle, cls: "text-destructive border-destructive/30 bg-destructive/10", label: "Inválido" };
  }
}

function ValidationsHistoryPage() {
  const { eventId } = Route.useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [event, setEvent] = useState<{ id: string; title: string } | null>(null);
  const [rows, setRows] = useState<Validation[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");
  const [resultFilter, setResultFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
    if (user) loadEvent();
  }, [user, loading, eventId]);

  useEffect(() => {
    if (user) load();
  }, [user, eventId, resultFilter, page]);

  async function loadEvent() {
    const { data: ev } = await supabase
      .from("events")
      .select("id, title")
      .eq("id", eventId)
      .single();
    if (ev) setEvent(ev);
  }

  async function load() {
    setLoadingData(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const res = await listEventValidations({
        data: {
          eventId,
          result: (resultFilter || undefined) as any,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
        headers,
      } as any);
      setRows(res.validations as Validation[]);
      setTotal(res.total);
    } catch (e: any) {
      setError(e.message || "Error al cargar historial");
      setRows([]);
    } finally {
      setLoadingData(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-3xl px-4 pb-20 pt-6 sm:px-6">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver al dashboard
      </Link>

      <div className="mb-6 flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl gradient-primary">
          <ClipboardList className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-xl font-black text-foreground">Historial de validaciones</h1>
          <p className="text-sm text-muted-foreground">{event?.title ?? "Cargando..."}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          value={resultFilter}
          onChange={(e) => {
            setPage(0);
            setResultFilter(e.target.value);
          }}
          className="rounded-lg border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {RESULT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-secondary-foreground hover:bg-accent"
        >
          <RefreshCw className="h-4 w-4" />
          Refrescar
        </button>
        <span className="ml-auto text-xs text-muted-foreground">
          {total} registro{total === 1 ? "" : "s"}
        </span>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loadingData ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No hay validaciones registradas para los filtros seleccionados.
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((v) => {
            const { Icon, cls, label } = resultMeta(v.result);
            const dt = new Date(v.validatedAt);
            return (
              <li key={v.id} className={`rounded-xl border p-3 ${cls}`}>
                <div className="flex items-start gap-3">
                  <Icon className="h-5 w-5 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-bold">{label}</span>
                      {v.validatedByRole && (
                        <span className="rounded-md border border-border bg-background/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {v.validatedByRole}
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        {dt.toLocaleString("es-AR", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    {v.message && (
                      <p className="mt-1 text-xs text-foreground/90">{v.message}</p>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      {v.ticketLabel && <span>Asiento/zona: <strong className="text-foreground">{v.ticketLabel}</strong></span>}
                      {v.ticketId && <span className="font-mono">ID: {v.ticketId.slice(0, 8)}</span>}
                      <span>
                        Validó: <strong className="text-foreground">{v.validatorDisplayName || v.validatorEmail || (v.validatedByUserId ? v.validatedByUserId.slice(0, 8) : "—")}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-lg border border-border bg-secondary px-3 py-2 text-xs text-secondary-foreground disabled:opacity-40"
          >
            Anterior
          </button>
          <span className="text-xs text-muted-foreground">
            Página {page + 1} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-border bg-secondary px-3 py-2 text-xs text-secondary-foreground disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
